import { Trans, useTranslation } from 'react-i18next';
import i18n from 'i18n';
import classNames from 'classnames';
import MembershipStatusTag from 'components/MembershipStatusTag';
import { MembershipStatus } from 'api/types/memberships/membership';

interface MembershipCardProps {
  orgImageUrl: string;
  orgName: string;
  membershipName: string;
  status?: MembershipStatus;
  startDate: string | Date;
  endDate: string | Date;
  holderName: string;
  holderImageUrl: string;
  onClick?: () => void;
}

const TIME_GATE_TEXT_KEYS = {
  fromTo: 'organizations:memberships.card.validFromTo',
  from: 'organizations:memberships.card.validFrom',
  until: 'organizations:memberships.card.validUntil',
  noTimeGate: 'organizations:memberships.membershipsTiers.accessOngoing'
};

const MembershipCard = ({
  orgImageUrl,
  orgName,
  membershipName,
  status,
  startDate,
  endDate,
  holderName,
  holderImageUrl,
  onClick
}: MembershipCardProps) => {
  let timeGateText = null;
  const { t } = useTranslation(['common']);

  if (startDate && endDate) {
    timeGateText = TIME_GATE_TEXT_KEYS.fromTo;
  } else if (startDate) {
    timeGateText = TIME_GATE_TEXT_KEYS.from;
  } else if (endDate) {
    timeGateText = TIME_GATE_TEXT_KEYS.until;
  } else {
    timeGateText = TIME_GATE_TEXT_KEYS.noTimeGate;
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const buttonProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: handleKeyDown
      }
    : {};

  return (
    <div
      className={classNames(
        'w-[332px] h-[209px] flex p-[2px] flex-col rounded-xl',
        onClick && 'cursor-pointer'
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
      style={{
        backgroundImage: `url(/img/user/membership-card-bg.svg)`,
        backgroundSize: 'cover'
      }}
    >
      <div className="flex flex-col px-3.5 pt-3.5">
        <div className="flex items-center">
          <div className="flex-1 flex-row gap-3 flex">
            <div
              className="w-[44px] h-[44px] rounded-[8px] overflow-hidden flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(to bottom right, rgb(93, 93, 93), rgb(33, 33, 33))'
              }}
            >
              <img
                src={orgImageUrl}
                alt={orgName}
                className="object-cover w-[40px] h-[40px] rounded-lg overflow-hidden"
              />
            </div>
            {status && <MembershipStatusTag status={status} />}
          </div>
          <img
            src="/img/logos/white-flat-logo.svg"
            alt="logo"
            className="w-[80px]"
          />
        </div>
      </div>
      <div className="flex flex-col px-3.5 pb-3.5 flex-1 justify-end">
        <div className="flex items-end">
          <div className="flex-1 pr-2">
            <div className="flex flex-col pb-2">
              <p className="text-sm font-bold text-white">{orgName}</p>
              <p className="text-sm font-normal text-white">{membershipName}</p>
            </div>
            {timeGateText && (
              <p className="text-xs font-normal text-white">
                <Trans
                  i18nKey={timeGateText}
                  values={{
                    startDate: new Date(startDate)
                      .toLocaleDateString(i18n.language, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })
                      .replace(/[.-]/g, '/'),
                    endDate: new Date(endDate)
                      .toLocaleDateString(i18n.language, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })
                      .replace(/[.-]/g, '/')
                  }}
                />
              </p>
            )}
            <p className="text-xs font-semibold text-gray-300 pt-6 uppercase">
              {holderName || t('common:unknown')}
            </p>
          </div>
          {/* TODO: Where the QR code will be */}
          <div className="w-[100px] h-[100px] bg-white rounded-full border-2 border-white">
            {holderImageUrl ? (
              <img
                src={holderImageUrl}
                alt={holderName}
                className="object-cover w-[96px] h-[96px] rounded-full overflow-hidden"
              />
            ) : (
              <img
                src="/img/illustrations/membership-card-holder-placeholder.png"
                className="object-cover w-[96px] h-[96px] rounded-full overflow-hidden"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCard;
